import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_BASE_URL;

// =====================================================
// INITIATE PAYMENT
// =====================================================

export const initiatePayment = createAsyncThunk(
  "phonepe/initiate",

  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_BASE}/payment/initiate`,
        payload
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: "Payment initiation failed",
        }
      );
    }
  }
);

// =====================================================
// GET USER SUBSCRIPTION DETAILS
// =====================================================

export const fetchUserSubscriptionDetails = createAsyncThunk(
  "phonepe/userSubscriptionDetails",

  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_BASE}/payment/subscription-details`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "SUBSCRIPTION API RESPONSE:",
        res.data
      );

      return res.data;
    } catch (error) {
      console.log(
        "SUBSCRIPTION API ERROR:",
        error.response?.data || error.message
      );

      return rejectWithValue(
        error.response?.data || {
          success: false,
          message: "Unable to fetch subscription details",
        }
      );
    }
  }
);

// =====================================================
// CHECK PAYMENT STATUS
// =====================================================

export const checkPaymentStatus = createAsyncThunk(
  "phonepe/status",

  async (transactionId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${API_BASE}/payment/status/${transactionId}`
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: "Unable to check payment status",
        }
      );
    }
  }
);

// =====================================================
// PAYMENT HISTORY
// =====================================================

export const fetchPaymentHistory = createAsyncThunk(
  "phonepe/history",

  async (userId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${API_BASE}/payment/history/${userId}`
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: "Unable to fetch payment history",
        }
      );
    }
  }
);

// =====================================================
// REFUND PAYMENT
// =====================================================

export const refundPayment = createAsyncThunk(
  "phonepe/refund",

  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_BASE}/payment/refund`,
        payload
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: "Refund failed",
        }
      );
    }
  }
);

// =====================================================
// DOWNLOAD INVOICE
// =====================================================

export const downloadInvoice = createAsyncThunk(
  "phonepe/invoice",

  async (transactionId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${API_BASE}/payment/invoice/${transactionId}`,
        {
          responseType: "blob",
        }
      );

      // Create PDF URL
      const url = window.URL.createObjectURL(
        new Blob([res.data], {
          type: "application/pdf",
        })
      );

      // Create download link
      const link = document.createElement("a");

      link.href = url;

      link.download = `invoice-${transactionId}.pdf`;

      document.body.appendChild(link);

      link.click();

      link.remove();

      // Release memory
      window.URL.revokeObjectURL(url);

      return true;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || {
          message: "Invoice download failed",
        }
      );
    }
  }
);

// =====================================================
// INITIAL STATE
// =====================================================

const initialState = {
  // ===================================================
  // PAYMENT
  // ===================================================

  loading: false,

  // ===================================================
  // PAYMENT STATUS
  // ===================================================

  statusLoading: false,

  // ===================================================
  // USER DETAILS
  // ===================================================

  userDetailsLoading: false,

  // ===================================================
  // SUBSCRIPTION
  // ===================================================

  subscriptionLoading: false,

  // ===================================================
  // PAYMENT HISTORY
  // ===================================================

  historyLoading: false,

  // ===================================================
  // GENERAL ERROR
  // ===================================================

  error: null,

  // ===================================================
  // PAYMENT DATA
  // ===================================================

  paymentUrl: null,

  transactionId: null,

  paymentData: null,

  status: null,

  amount: null,

  // ===================================================
  // INVOICE
  // ===================================================

  invoiceUrl: null,

  invoiceNumber: null,

  // ===================================================
  // USER
  // ===================================================

  userDetails: null,

  // ===================================================
  // CURRENT SUBSCRIPTION / PLAN
  // ===================================================

  planDetails: null,

  subscriptionDetails: null,

  // ===================================================
  // PAYMENT HISTORY
  // ===================================================

  paymentHistory: [],

  // ===================================================
  // REFUND
  // ===================================================

  refundStatus: null,
};

// =====================================================
// SLICE
// =====================================================

const phonepeSlice = createSlice({
  name: "phonepe",

  initialState,

  reducers: {
    // =================================================
    // CLEAR ALL PHONEPE STATE
    // =================================================

    clearPhonePeState: (state) => {
      state.loading = false;

      state.statusLoading = false;

      state.userDetailsLoading = false;

      state.subscriptionLoading = false;

      state.historyLoading = false;

      state.error = null;

      state.paymentUrl = null;

      state.transactionId = null;

      state.paymentData = null;

      state.status = null;

      state.amount = null;

      state.invoiceUrl = null;

      state.invoiceNumber = null;

      state.userDetails = null;

      state.planDetails = null;

      state.subscriptionDetails = null;

      state.paymentHistory = [];

      state.refundStatus = null;
    },

    // =================================================
    // CLEAR USER / SUBSCRIPTION DETAILS ONLY
    // =================================================

    clearUserSubscription: (state) => {
      state.userDetails = null;

      state.planDetails = null;

      state.subscriptionDetails = null;

      state.userDetailsLoading = false;

      state.subscriptionLoading = false;

      state.error = null;
    },

    // =================================================
    // CLEAR PAYMENT DATA
    // =================================================

    clearPaymentData: (state) => {
      state.paymentUrl = null;

      state.transactionId = null;

      state.paymentData = null;

      state.status = null;

      state.amount = null;

      state.invoiceUrl = null;

      state.invoiceNumber = null;
    },
  },

  extraReducers: (builder) => {
    // =================================================
    // INITIATE PAYMENT
    // =================================================

    builder

      .addCase(
        initiatePayment.pending,
        (state) => {
          state.loading = true;

          state.error = null;
        }
      )

      .addCase(
        initiatePayment.fulfilled,
        (state, action) => {
          state.loading = false;

          const data =
            action.payload || {};

          state.paymentUrl =
            data.paymentUrl || null;

          state.transactionId =
            data.transactionId || null;

          state.paymentData =
            data;

          state.error = null;
        }
      )

      .addCase(
        initiatePayment.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload?.message ||
            "Payment initiation failed";
        }
      );

    // =================================================
    // USER SUBSCRIPTION DETAILS
    // =================================================

    builder

      .addCase(
        fetchUserSubscriptionDetails.pending,
        (state) => {
          state.subscriptionLoading = true;

          state.subscriptionDetails = null;

          state.error = null;
        }
      )

      .addCase(
        fetchUserSubscriptionDetails.fulfilled,
        (state, action) => {
          state.subscriptionLoading = false;

          console.log(
            "REDUX SUBSCRIPTION:",
            action.payload
          );

          state.subscriptionDetails =
            action.payload;

          state.error = null;
        }
      )

      .addCase(
        fetchUserSubscriptionDetails.rejected,
        (state, action) => {
          state.subscriptionLoading = false;

          state.subscriptionDetails = null;

          state.error =
            action.payload?.message ||
            "Failed to fetch subscription details";
        }
      );

    // =================================================
    // CHECK PAYMENT STATUS
    // =================================================

    builder

      .addCase(
        checkPaymentStatus.pending,
        (state) => {
          state.statusLoading = true;

          state.error = null;
        }
      )

      .addCase(
        checkPaymentStatus.fulfilled,
        (state, action) => {
          state.statusLoading = false;

          const data =
            action.payload || {};

          // Payment state
          state.status =
            data.state || "PENDING";

          // Complete response
          state.paymentData =
            data;

          // Transaction
          state.transactionId =
            data.transactionId ||
            null;

          // Amount
          state.amount =
            data.amount || 0;

          // Invoice
          state.invoiceUrl =
            data.invoiceUrl ||
            null;

          state.invoiceNumber =
            data.invoiceNumber ||
            null;

          // User
          state.userDetails =
            data.user || {
              name: null,
              username: null,
              email: null,
              phone: null,
            };

          // Plan
          state.planDetails =
            data.plan || {
              planId: null,
              planName: null,
            };

          state.error = null;
        }
      )

      .addCase(
        checkPaymentStatus.rejected,
        (state, action) => {
          state.statusLoading = false;

          state.error =
            action.payload?.message ||
            "Status fetch failed";
        }
      );

    // =================================================
    // PAYMENT HISTORY
    // =================================================

    builder

      .addCase(
        fetchPaymentHistory.pending,
        (state) => {
          state.historyLoading = true;

          state.error = null;
        }
      )

      .addCase(
        fetchPaymentHistory.fulfilled,
        (state, action) => {
          state.historyLoading = false;

          state.paymentHistory =
            action.payload?.payments || [];

          state.error = null;
        }
      )

      .addCase(
        fetchPaymentHistory.rejected,
        (state, action) => {
          state.historyLoading = false;

          state.error =
            action.payload?.message ||
            "Failed to fetch payment history";
        }
      );

    // =================================================
    // REFUND
    // =================================================

    builder

      .addCase(
        refundPayment.pending,
        (state) => {
          state.error = null;

          state.refundStatus = null;
        }
      )

      .addCase(
        refundPayment.fulfilled,
        (state, action) => {
          state.refundStatus =
            action.payload;

          state.error = null;
        }
      )

      .addCase(
        refundPayment.rejected,
        (state, action) => {
          state.error =
            action.payload?.message ||
            "Refund failed";
        }
      );

    // =================================================
    // INVOICE DOWNLOAD
    // =================================================

    builder

      .addCase(
        downloadInvoice.pending,
        (state) => {
          state.error = null;
        }
      )

      .addCase(
        downloadInvoice.fulfilled,
        (state) => {
          state.error = null;
        }
      )

      .addCase(
        downloadInvoice.rejected,
        (state, action) => {
          state.error =
            action.payload?.message ||
            "Invoice download failed";
        }
      );
  },
});

// =====================================================
// EXPORT ACTIONS
// =====================================================

export const {
  clearPhonePeState,
  clearUserSubscription,
  clearPaymentData,
} = phonepeSlice.actions;

// =====================================================
// EXPORT REDUCER
// =====================================================

export default phonepeSlice.reducer;

